import { Alert, Box, Chip, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import { useState } from "react";
import { useDirtTalkRecent } from "../hooks/useApiQueries.js";
import { SEO } from "../components/SEO.js";
import { DirtTalkCard } from "../components/DirtTalkCard.js";
import { NeonEyebrow, SectionHeading, NEON_GLOW_SOFT } from "../components/Neon.js";

const VARIANTS = [
  { value: "ALL", label: "All" },
  { value: "MACHINE_OF_DAY", label: "Machine of the Day" },
  { value: "DID_YOU_KNOW", label: "Did You Know" },
  { value: "THIS_DAY_IN_DIRT", label: "This Day in Dirt" },
] as const;

export function DirtTalkArchive() {
  const { data, isLoading } = useDirtTalkRecent();
  const [filter, setFilter] = useState<string>("ALL");
  const items = data?.items ?? [];
  const filtered = filter === "ALL" ? items : items.filter((i) => i.variant === filter);
  const hero = filtered[0] ?? null;
  const rest = filtered.slice(1);

  return (
    <>
      <SEO
        title="Dirt Talk"
        description="Daily earthmoving cards from Corian Park — Machine of the Day, Did You Know, This Day in Dirt."
        canonical="https://corianpark.au/dirt-talk"
      />

      <Box sx={{ mb: 4 }}>
        <NeonEyebrow glow>DAILY · CURATED</NeonEyebrow>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: 44, md: 64 },
            fontWeight: 900,
            mt: 1,
            letterSpacing: "-0.02em",
            lineHeight: 1.05,
          }}
        >
          Dirt Talk
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 600 }}>
          Three cards a day on Australian earthmoving — the machine of the day, a "did you
          know" tidbit, and a moment from earthmoving history.
        </Typography>
      </Box>

      {/* Variant filter */}
      <Stack direction="row" spacing={1} sx={{ mb: 4, flexWrap: "wrap", gap: 1 }}>
        {VARIANTS.map((v) => (
          <Chip
            key={v.value}
            label={v.label}
            onClick={() => setFilter(v.value)}
            color={filter === v.value ? "primary" : "default"}
            variant={filter === v.value ? "filled" : "outlined"}
            sx={{
              fontWeight: 700,
              ...(filter === v.value ? { boxShadow: NEON_GLOW_SOFT } : {}),
            }}
          />
        ))}
      </Stack>

      {isLoading ? (
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
      ) : items.length === 0 ? (
        <Alert severity="info">
          No Dirt Talk published yet — check back soon. New cards publish daily at 08:30 AEST.
        </Alert>
      ) : filtered.length === 0 ? (
        <Alert severity="info">No cards match this filter yet.</Alert>
      ) : (
        <>
          {/* Hero card — first/most-recent */}
          {hero && (
            <Box sx={{ mb: 5 }}>
              <NeonEyebrow sx={{ display: "block", mb: 1 }}>LATEST</NeonEyebrow>
              <RouterLink
                to={`/dirt-talk/${hero.date}`}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Box
                  sx={{
                    transition: "transform 200ms ease, box-shadow 200ms ease",
                    "&:hover": { transform: "translateY(-2px)", boxShadow: NEON_GLOW_SOFT },
                  }}
                >
                  <DirtTalkCard
                    variant={hero.variant}
                    date={hero.date}
                    title={hero.title}
                    body={hero.body}
                    imageUrl={hero.imageUrl}
                    itemId={hero.id}
                    showSaveButton
                  />
                </Box>
              </RouterLink>
            </Box>
          )}

          {rest.length > 0 && (
            <>
              <SectionHeading eyebrow="MORE FROM THE ARCHIVE" title="Earlier days" />
              <Grid container spacing={3}>
                {rest.map((i) => (
                  <Grid key={i.id} size={{ xs: 12, md: 6 }}>
                    <RouterLink
                      to={`/dirt-talk/${i.date}`}
                      style={{ textDecoration: "none", color: "inherit" }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          transition: "transform 200ms ease, box-shadow 200ms ease",
                          "&:hover": { transform: "translateY(-2px)", boxShadow: NEON_GLOW_SOFT },
                        }}
                      >
                        <DirtTalkCard
                          variant={i.variant}
                          date={i.date}
                          title={i.title}
                          body={i.body}
                          imageUrl={i.imageUrl}
                          itemId={i.id}
                          showSaveButton
                        />
                      </Box>
                    </RouterLink>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </>
      )}
    </>
  );
}
